import axios from 'axios';
import { baseURL } from '../config/api.config';
import { ERROR_MESSAGES } from '../constants/messages.const';
import { redirectToLogin } from '../utils/authRedirect';

let siteURL = window.location.origin;

// Force HTTPS for localhost in development
if (siteURL.startsWith("http://") && window.location.hostname === "localhost") {
  siteURL = siteURL.replace("http://", "https://");
}

// Create Axios instance
const API = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    "X-Host": siteURL,
  },
  timeout: 60000,
});

// Request interceptor to add Authorization header
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * 401 → token refresh → isteği yeniden deneme akışı.
 *
 * Access token süresi dolduğunda kullanıcıyı login'e atmak yerine refresh
 * token ile sessizce yeni token alınır ve başarısız olan istek aynen tekrar
 * edilir. Aynı anda birden fazla istek 401 aldığında yalnızca BİR refresh
 * çağrısı yapılır (single-flight); diğer istekler kuyruğa girer ve yeni
 * token geldiğinde topluca yeniden denenir.
 *
 * Refresh de başarısız olursa (refresh token geçersiz/süresi dolmuş) oturum
 * verileri temizlenip login sayfasına yönlendirilir — kullanıcı hiçbir
 * durumda beyaz ekranda bırakılmaz.
 */
let isRefreshing = false;
let refreshWaiters = [];

const notifyRefreshWaiters = (error, token) => {
  refreshWaiters.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token)
  );
  refreshWaiters = [];
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Refresh başarısız olduğunda başka bir sekmenin bu arada rotasyonu
 * tamamlayıp storage'a taze token yazmış olma ihtimalini kontrol eder.
 * Backend refresh token'ı tek kullanımlık yaptığı için iki sekme aynı çiftle
 * refresh atarsa ikincisi reddedilir; bu durumda oturumu bitirmek yerine
 * kazanan sekmenin yazdığı token kullanılmalıdır. Kazananın yanıtı henüz
 * gelmemiş olabilir; bu yüzden kısa bir bekleme sonrası ikinci kez bakılır.
 */
const findTokenRotatedByOtherTab = async (usedRefreshToken) => {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const storedToken = localStorage.getItem("token");
    const storedRefreshToken = localStorage.getItem("refreshToken");
    if (
      storedToken &&
      storedRefreshToken &&
      storedRefreshToken !== usedRefreshToken
    ) {
      return storedToken;
    }
    if (attempt === 0) {
      await wait(750);
    }
  }
  return null;
};

const refreshAccessToken = async () => {
  const token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");

  if (!token || !refreshToken) {
    throw new Error("Oturum bilgisi bulunamadı");
  }

  try {
    // Ham axios kullanılıyor; API instance kullanılsaydı refresh çağrısının
    // kendisi de bu interceptor'dan geçip sonsuz döngü riski oluştururdu.
    const response = await axios.post(
      `${baseURL}/auth/public/refresh`,
      { token, refreshToken },
      {
        headers: { "Content-Type": "application/json", "X-Host": siteURL },
        timeout: 60000,
      }
    );

    // Backend ResponseData<TokenDto> döner: { data: { token, refreshToken, ... } }
    const tokenData = response.data?.data;
    if (!tokenData?.token) {
      throw new Error("Refresh yanıtında token yok");
    }

    localStorage.setItem("token", tokenData.token);
    if (tokenData.refreshToken) {
      localStorage.setItem("refreshToken", tokenData.refreshToken);
    }
    if (tokenData.expireSecond) {
      localStorage.setItem("expireSecond", tokenData.expireSecond);
    }

    return tokenData.token;
  } catch (refreshError) {
    const rotatedToken = await findTokenRotatedByOtherTab(refreshToken);
    if (rotatedToken) {
      return rotatedToken;
    }
    throw refreshError;
  }
};

// Response interceptor for error handling
API.interceptors.response.use(
  (response) => ({ ...response.data, isHttpSuccess: true }),
  async (error) => {
    const status = error.response?.status || 0;
    const message =
      error.response?.data?.message ||
      ERROR_MESSAGES[status] ||
      ERROR_MESSAGES.default;

    // Handle Axios timeout
    if (error.code === "ECONNABORTED") {
      return Promise.reject({
        status: 504,
        message: ERROR_MESSAGES[504],
        error,
        isHttpError: true,
      });
    }

    const config = error.config;
    const isPublicRequest = (config?.url || "").includes("/public/");

    // Public akışlardaki (login, şifremi unuttum vb.) 401'ler oturum
    // sonlanması değil iş kuralı hatasıdır; ilgili sayfa yönetir.
    if (status === 401 && config && !isPublicRequest && !config._retry) {
      // Korumalı istek 401 aldı ama elimizde refresh token yok (ör. başka
      // sekme çıkış yaptı): refresh denemeden doğrudan login'e dön; yoksa
      // SPA bellek içi "authenticated" durumuyla boş ekranlarda kalır.
      if (!localStorage.getItem("refreshToken")) {
        redirectToLogin();
        return Promise.reject({ status, message, error, isHttpError: true });
      }

      config._retry = true;

      try {
        let newToken;
        if (isRefreshing) {
          // Refresh zaten sürüyor; bitmesini bekle
          newToken = await new Promise((resolve, reject) => {
            refreshWaiters.push({ resolve, reject });
          });
        } else {
          isRefreshing = true;
          try {
            newToken = await refreshAccessToken();
            notifyRefreshWaiters(null, newToken);
          } catch (refreshError) {
            notifyRefreshWaiters(refreshError, null);
            redirectToLogin();
            throw refreshError;
          } finally {
            isRefreshing = false;
          }
        }

        config.headers.Authorization = `Bearer ${newToken}`;
        return API(config);
      } catch (retryError) {
        return Promise.reject({ status, message, error, isHttpError: true });
      }
    }

    return Promise.reject({ status, message, error, isHttpError: true });
  }
);

export { API };
